import React from "react";
import { Navigate } from "react-router-dom";

import RequireAuth from "../../components/RequireAuth/RequireAuth";
import RequirePermission from "../../components/RequirePermission/RequirePermission";
import RequireFleetStateAuth from "../../components/RequireFleetStateAuth/RequireFleetStateAuth";
import {
  FleetStateAuthProvider,
  FleetStateNotConfigured,
} from "../../auth/fleetStateAuth";
import {
  AUTH_PROVIDER,
  DEV_UNLOCK_TRACTOR_STATE,
  FLEET_STATE_CONFIGURED,
  TRACTOR_STATE_ENABLED,
} from "../../utils/devFlags";
import TractorState from "./TractorState";
import TractorStateLive from "./TractorStateLive";
import TractorStateAppSession from "./TractorStateAppSession";

/**
 * Which Tractor State this build serves, and which session authorizes it.
 *
 * Extracted from App.tsx so a test can assert the choice. It used to be an
 * inline expression in the route table, which meant nothing could check the one
 * property that matters here: that exactly ONE OIDC provider is mounted. A test
 * of the components alone passes either way -- both work in isolation -- which
 * is precisely how SW-7017 reached beta.
 */
export default function TractorStateRoute() {
  return (
    // Present in beta and local; in prod only when the build was given
    // the fleet_state API values (see TRACTOR_STATE_ENABLED in
    // utils/devFlags).
    //
    // Configured wins: any build with the fleet_state API and Cognito
    // values set is live-only, so the deployed beta app and a local
    // `npm run start-beta` both read the real fleet and neither can
    // reach the committed snapshot. The page authenticates against the
    // fleet_state Cognito pool (Google SSO) — a page-scoped login,
    // separate from the app's Auth0, that authorizes the read API
    // (SW-6110) -- on the Auth0 path only. Where AUTH_PROVIDER is
    // "cognito" the app session already comes from that pool, so this
    // page uses it directly and mounts no second provider.
    //
    // Unconfigured is isolated dev (`npm run start-dev`): no API to
    // call, so the page renders the snapshot read-only, and DEV_UNLOCK
    // waives the permission gate that a signed-out offline session
    // can't satisfy. A build that is neither says so rather than
    // inventing a fleet.
    //
    // The Cognito login authorizes the read API; it does NOT stand in
    // for the app session. This page still renders the app's shared
    // Navbar, which draws its identity and membership from Auth0, so it
    // needs RequireAuth exactly like every other page. Without it the
    // navbar rendered degraded here — no email, and the Flights and
    // Tractor State links missing while standing ON Tractor State —
    // whenever Auth0's silent session restore hadn't landed, with
    // nothing to repair it. The permission gate matches the Navbar
    // link's so the route and the nav can't disagree.
    !TRACTOR_STATE_ENABLED ? (
      <Navigate to="/dashboard" replace />
    ) : FLEET_STATE_CONFIGURED ? (
      <RequireAuth>
        <RequirePermission permission="read:aerovect-vehicles">
          {/*
                    One OIDC provider on this route, never two.

                    When the whole app is already on Cognito, the app session IS
                    a fleet_state session -- same pool, same app client -- so the
                    page-scoped login below is redundant. Mounting it anyway put
                    two AuthProviders on the page, one keyed in sessionStorage
                    and one in localStorage, and they fought over the `?code=`
                    that came back: the root provider processed a state belonging
                    to the other and failed with "No matching state found in
                    storage" (SW-7017).

                    On the Auth0 path the page-scoped login is still how this
                    page gets a Cognito token at all, so that subtree is
                    unchanged.
                  */}
          {AUTH_PROVIDER === "cognito" ? (
            <TractorStateAppSession />
          ) : (
            <FleetStateAuthProvider>
              <RequireFleetStateAuth>
                <TractorStateLive />
              </RequireFleetStateAuth>
            </FleetStateAuthProvider>
          )}
        </RequirePermission>
      </RequireAuth>
    ) : DEV_UNLOCK_TRACTOR_STATE ? (
      <TractorState />
    ) : (
      <FleetStateNotConfigured />
    )
  );
}
